import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { getBaseQuery, getBaseUrl } from '../../../apis/api.util.ts'
import {
  ColGroupDef,
  GridReadyEvent,
  IGetRowsParams,
  ValueFormatterParams,
} from 'ag-grid-enterprise'
import {
  GetStatisticsRequest,
  GetStatisticsResponse,
} from '../../../apis/statistics/statistics-api.def.ts'
import { selectManagementInput } from '../../../redux/slices/management-input.slice.ts'
import { useSelector } from 'react-redux'
import { useCallback, useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import { BaseQueryApi } from '@reduxjs/toolkit/query'
import { ColDef, GridApi } from 'ag-grid-community'
import {
  coloredNumberCellStyle,
  currencyValueFormatter,
  millionFormatter,
  numberCellStyle,
  percentageValueFormatter,
} from '../../../utils/formatters.ts'
import { CustomCellRendererProps } from 'ag-grid-react'
import { StatisticsActionColumn } from './statistics-action-column.tsx'
import { BookNameCellRenderer } from '../../components/book-name-cell-renderer.tsx'
import { PeriodDependentHeaderComponent } from './statistics-period-dependent-header.tsx'
import './statistics-table.css'
import { useTranslation } from 'react-i18next'
import { STATISTICS_LIST_COLUMN_STATE_KEY } from '../../../utils/constants.ts'
import { IsTrialCellRenderer } from '../../components/is-trial-cell-renderer.tsx'
import { selectUser } from '../../../features/user/user.slice.ts'
import { useAppSelector } from '../../../redux/hooks.ts'

type ColumnOrGroup = ColDef | (ColGroupDef & { children: ColumnOrGroup[] })

function flattenColumns(groups: ColumnOrGroup[]): ColDef[] {
  return groups.flatMap((group) =>
    'children' in group && group.children
      ? flattenColumns(group.children)
      : [group as ColDef]
  )
}

function reorderGroupChildren(
  groups: ColumnOrGroup[],
  orderedFields: string[]
): ColumnOrGroup[] {
  return groups.map((group) => {
    if ('children' in group && group.children) {
      const childrenWithField = group.children.filter(
        (c): c is ColDef => 'field' in c && !!c.field
      )
      const childrenWithoutField = group.children.filter(
        (c) => !('field' in c && c.field)
      )
      const reorderedChildren = orderedFields
        .map((field) => childrenWithField.find((c) => c.field === field))
        .filter((c): c is ColDef => !!c)
      return {
        ...group,
        children: [...reorderedChildren, ...childrenWithoutField],
      }
    }
    return group
  })
}

function buildStatisticListUrl(request: GetStatisticsRequest) {
  let region = request.region ?? ''
  let netCategory = request.netCategory ?? ''

  // Parse netCategory if it contains " - " separator
  if (request.netCategory && request.netCategory.includes(' - ')) {
    const parts = request.netCategory.split(' - ')
    region = parts[0] // "Greater China"
    netCategory = parts[1] // "Low Net"
  }

  const params = new URLSearchParams({
    page: String(request.page),
    pageSize: String(request.pageSize ?? ''),
    startDate: request.startDate ?? '',
    endDate: request.endDate ?? '',
    region: region,
    netCategory: netCategory,
    firmId: request.firmId ?? '',
    isTrial: request.isTrial ?? '',
  })
  return `/?${params.toString()}`
}

interface StatisticsTableProps {
  height: number
}

export const StatisticsTable: React.FC<StatisticsTableProps> = ({ height }) => {
  const { t } = useTranslation()
  const gridApiRef = useRef<GridApi | null>(null)
  const managementInput = useSelector(selectManagementInput)
  const userInfo = useAppSelector(selectUser)

  const columnDefs: ColDef[] = useMemo(() => {
    const baseColumns = [
      {
        headerName: t('statistics.statistics_table.columns.static_information'),
        children: [
          {
            headerName: '',
            field: 'bookId',
            pinned: 'left',
            maxWidth: 55,
            cellRenderer: (props: CustomCellRendererProps) => {
              if (props.value !== undefined && props.data.status === 'Active') {
                return (
                  <StatisticsActionColumn
                    data={{
                      bookId: props.data.bookId,
                      userName:
                        props.data.userName || props.data.bookName || '',
                      firmName: props.data.firmName || '',
                    }}
                  />
                )
              }
              return <></>
            },
          },
          {
            headerName: t('statistics.statistics_table.columns.book_name'),
            field: 'bookName',
            pinned: 'left',
            cellRenderer: BookNameCellRenderer,
          },
          {
            headerName: t('statistics.statistics_table.columns.firm_name'),
            field: 'firmName',
          },
          {
            headerName: t('statistics.statistics_table.columns.gmv'),
            field: 'gmv',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              millionFormatter(params, { decimal: 2 }),
          },
          {
            headerName: t('statistics.statistics_table.columns.net_on_gmv_pct'),
            field: 'netGMVRate',
            type: 'numberColumn',
            cellStyle: numberCellStyle,
            valueFormatter: percentageValueFormatter,
          },
          {
            headerName: t('statistics.statistics_table.columns.avg_duration'),
            field: 'avgDur',
            type: 'numberColumn',
          },
          {
            headerName: t('statistics.statistics_table.columns.bp'),
            field: 'bp',
            valueFormatter: (params: ValueFormatterParams) =>
              millionFormatter(params, { decimal: 2 }),
            type: 'numberColumn',
          },
          {
            headerName: t('statistics.statistics_table.columns.bp_pct'),
            field: 'bpRate',
            valueFormatter: percentageValueFormatter,
            type: 'numberColumn',
          },
          {
            headerName: t('statistics.statistics_table.columns.daily_pnl'),
            field: 'dailyPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.wtd_pnl'),
            field: 'wtdPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.mtd_pnl'),
            field: 'mtdPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.qtd_pnl'),
            field: 'qtdPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.ytd_pnl'),
            field: 'ytdPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('statistics.statistics_table.columns.account_type'),
            field: 'isTrial',
            suppressHeaderMenuButton: true,
            cellRenderer: IsTrialCellRenderer,
            maxWidth: 80,
          },
        ],
      },
      {
        headerName: t(
          'statistics.statistics_table.columns.period_dependent_data'
        ),
        headerGroupComponentParams: {
          innerHeaderGroupComponent: PeriodDependentHeaderComponent,
          innerHeaderGroupComponentParams: {
            startDate: managementInput.dateRange[0],
            endDate: managementInput.dateRange[1],
          },
        },
        headerClass: 'period-dependent-header',
        children: [
          {
            headerName: t('statistics.statistics_table.columns.avg_bp_rate'),
            headerClass: 'period-dependent-child-header',
            field: 'avgBPRate',
            type: 'numberColumn',
            cellStyle: numberCellStyle,
            valueFormatter: percentageValueFormatter,
          },
          {
            headerName: t('statistics.statistics_table.columns.hit_rate'),
            headerClass: 'period-dependent-child-header',
            field: 'hitRate',
            type: 'numberColumn',
            cellStyle: numberCellStyle,
            valueFormatter: percentageValueFormatter,
          },
          {
            headerName: t(
              'statistics.statistics_table.columns.abs_return_rate'
            ),
            headerClass: 'period-dependent-child-header',
            field: 'absReturnRate',
            type: 'numberColumn',
            cellStyle: numberCellStyle,
            valueFormatter: percentageValueFormatter,
          },
          {
            headerName: t('statistics.statistics_table.columns.total_pnl'),
            headerClass: 'period-dependent-child-header',
            field: 'totalPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
        ],
      },
    ]

    // Only show account type for internal admin users
    if (userInfo.role === '') {
      baseColumns[0].children = baseColumns[0].children.filter(
        (col) => col.field !== 'isTrial'
      )
    }

    const savedState = localStorage.getItem(STATISTICS_LIST_COLUMN_STATE_KEY)
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        const colOrder = state.map((col: { colId: string }) => col.colId)
        // Flatten all leaf columns
        const flatColumns = flattenColumns(baseColumns)
        // Only keep fields that exist in the current columns
        const validOrder = colOrder.filter((id: string) =>
          flatColumns.some((col) => col.field === id)
        )
        // Rebuild groups with reordered children
        return reorderGroupChildren(baseColumns, validOrder)
      } catch {
        return baseColumns
      }
    }

    return baseColumns
  }, [userInfo, managementInput.dateRange, t])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      minWidth: 200,
      sortable: false,
      resizable: true,
      filter: false,
    }),
    []
  )

  const pageSize = 20
  const baseQuery = getBaseQuery(getBaseUrl('statistics'))

  const datasource = useMemo(
    () => ({
      getRows: async (params: IGetRowsParams) => {
        const isFirstPage = params.startRow === 0
        if (isFirstPage) {
          gridApiRef.current?.showLoadingOverlay()
        }

        try {
          const request: GetStatisticsRequest = {
            page: Math.floor(params.startRow / pageSize) + 1,
            pageSize: pageSize,
            startDate: dayjs(managementInput.dateRange[0]).format('YYYY-MM-DD'),
            endDate: dayjs(managementInput.dateRange[1]).format('YYYY-MM-DD'),
            netCategory: managementInput.category,
            firmId: managementInput.firm,
            isTrial: managementInput.accountStatisticsTrialType,
          }

          const result = await baseQuery(
            {
              url: buildStatisticListUrl(request),
              method: 'GET',
            },
            {} as BaseQueryApi,
            {}
          )

          const dataResult = result as { data?: GetStatisticsResponse }

          if (dataResult && dataResult.data && dataResult.data.data) {
            const statisticItems = dataResult.data.data.statisticsItems
            const lastRow =
              statisticItems.length < pageSize
                ? params.startRow + statisticItems.length
                : undefined
            params.successCallback(statisticItems, lastRow)
            gridApiRef.current?.hideOverlay()
          } else {
            params.failCallback()
            if (isFirstPage) {
              gridApiRef.current?.hideOverlay()
            }
          }
        } catch {
          params.failCallback()
          if (isFirstPage) {
            gridApiRef.current?.hideOverlay()
          }
        }
      },
    }),
    [
      managementInput.dateRange,
      managementInput.firm,
      managementInput.category,
      pageSize,
      baseQuery,
    ]
  )

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api
    const savedState = localStorage.getItem(STATISTICS_LIST_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)
      gridApiRef.current.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }, [])

  const handleColumnChanged = useCallback(() => {
    if (!gridApiRef.current) return
    const state = gridApiRef.current.getColumnState()
    localStorage.setItem(
      STATISTICS_LIST_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }, [])

  const handleSortChanged = useCallback(() => {
    if (!gridApiRef.current) return
    const state = gridApiRef.current.getColumnState()
    localStorage.setItem(
      STATISTICS_LIST_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }, [])

  return (
    <IdeyaAgGridTable
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      datasource={datasource}
      rowModelType={'infinite'}
      cacheBlockSize={pageSize}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onSortChanged={handleSortChanged}
      height={height}
    />
  )
}
